import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { gql, useQuery } from '@apollo/client';
import CloseIcon from '@mui/icons-material/Close';
import { isNil, isUndefined } from 'lodash/fp';
import {
  Button, Dialog, DialogContent, DialogTitle, Form, IconButton, MenuItem, SelectField,
} from '../../../../2-component';
import { Box, Typography } from '../../../../1-primative';
import { EditField, FieldTypes } from '../../../../4-module/configurableOptionFields/optionEditComponent';
import { WidgetOptions } from '../../../../../interfaces/pageConfiguration';
import { homePageInitialConfigs } from './homePageInitialConfigs';

const FETCH_HOME_PAGE_WIDGET_OPTIONS = gql`
  query fetchHomePageWidgetOptions($widgetType: HomePageWidgetConfigurationTypes!) {
    fetchHomePageWidgetOptions(widgetType: $widgetType) {
      widgetOptions {
        default
        key
        options
        showIf
        requiredIf
        disabledIf
        showWith
        optionType
        info {
          en
          fr
        }
        label {
          en
          fr
        }
      }
    }
  }
`;

// Home page only supports FREE_FORM_INPUT
const HOME_PAGE_WIDGET_TYPES = [
  'FREE_FORM_INPUT',
  'MARKET_DATA_TICKER',
  'QUICK_LINKS',
  'AUM_EXPLORER',
  'FEES_AND_REVENUE',
  'NETFLOWS',
  'OPEN_WORKFLOWS',
  'REPORT',
  'PENDING_TRANSACTIONS',
  'PRACTICE_OVERVIEW',
];

interface HomePageWidget {
  type: string;
  options: any;
}

interface NewHomePageWidgetProps {
  setWidgets: (widgets: HomePageWidget[]) => void;
  widgets: HomePageWidget[];
  activeWidget?: HomePageWidget;
  index?: number;
  open: boolean;
  setOpen: (open: boolean) => void;
  onSave: () => void;
}

const checkShowIfHierarchy = (x: any, options: any): boolean => {
  if (isNil(x.showIf)) return true;

  const parentOption = options.find((ele: any) => ele.key === Object.keys(x.showIf)[0]);
  if (parentOption?.value !== Object.values(x.showIf)[0]) {
    return false;
  }

  return checkShowIfHierarchy(parentOption, options);
};

export const NewHomePageWidget = ({
  setWidgets,
  widgets,
  activeWidget = { type: '', options: {} },
  index,
  open,
  setOpen,
  onSave,
}: NewHomePageWidgetProps) => {
  const { t } = useTranslation(['pageConfiguration', 'homePageConfiguration']);
  const [options, setOptions] = useState<WidgetOptions[]>([]);
  const [widget, setWidget] = useState<HomePageWidget>(activeWidget);

  const { data } = useQuery(FETCH_HOME_PAGE_WIDGET_OPTIONS, {
    variables: { widgetType: widget.type },
    fetchPolicy: 'network-only',
    skip: !widget.type,
  });

  useEffect(() => {
    if (data) {
      const newOptions: WidgetOptions[] = [...(data?.fetchHomePageWidgetOptions?.widgetOptions || [])];

      newOptions.forEach((x: WidgetOptions, i: number) => {
        newOptions[i] = {
          ...x,
          value: x.key && !isUndefined(widget?.options[x.key]) ? widget?.options[x.key] : x.default,
        };
      });

      if (JSON.stringify(options) !== JSON.stringify(newOptions)) {
        setOptions(newOptions);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, widget.options]);

  useEffect(() => {
    if (options) {
      const newFormattedOptions: any = {};
      options.forEach((x: any) => {
        newFormattedOptions[x.key] = x.value;
      });

      if (JSON.stringify(widget.options) !== JSON.stringify(newFormattedOptions)) {
        setWidget({ ...widget, options: newFormattedOptions });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options]);

  useEffect(() => {
    setWidget(activeWidget);
    if (activeWidget.type === '') {
      setOptions([]);
    }
  }, [activeWidget, open]);

  const reset = () => {
    setWidget({ type: '', options: {} });
    setOptions([]);
    setOpen(false);
  };

  const onSubmit = () => {
    if (!widget.options) {
      widget.options = homePageInitialConfigs[widget.type];
    }

    const translatedStringOptions = options.filter((elem: any) => elem.optionType === FieldTypes.TRANSLATED_STRING);
    const validateTranslatedStringOptions = translatedStringOptions.every((elem: any) => {
      const translatedValue = widget.options[elem.key]?.en;
      if (elem.requiredIf) {
        const [key, expectedValue] = Object.entries(elem.requiredIf)[0];
        const conditionIsMet = isNil(widget.options[key]) || widget.options[key] === expectedValue;
        return !conditionIsMet || Boolean(translatedValue);
      }
      return Boolean(translatedValue);
    });

    if (validateTranslatedStringOptions) {
      if (typeof index === 'number') {
        const updatedWidgets = [...widgets];
        updatedWidgets[index] = widget;
        setWidgets(updatedWidgets);
      } else {
        const newWidgets = [...widgets, widget];
        setWidgets(newWidgets);
      }
      reset();
      onSave();
    }
  };

  return (
    <Dialog open={open} onClose={reset} maxWidth='md' fullWidth>
      <DialogTitle>
        <Box display='flex' justifyContent='space-between' width='100%' alignItems='center'>
          <Typography variant='headingSmall' sx={{ mr: 0.5 }}>
            {t('homePageConfiguration:widget.addWidget')}
          </Typography>
          <IconButton onClick={reset} aria-label='close' size='medium'>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Form onSubmit={onSubmit}>
          <SelectField
            fullWidth
            label={t('pageConfiguration:widgetType')}
            value={widget.type}
            onChange={(e: any) => setWidget({ ...widget, type: e.target.value, options: {} })}
          >
            {HOME_PAGE_WIDGET_TYPES.map((type: string) => (
              <MenuItem key={type} value={type}>
                {t(`pageConfiguration:widgetTypes.${type}`)}
              </MenuItem>
            ))}
          </SelectField>

          {options.length > 0 && (
            <Box mt={2}>
              <Typography variant='bodyMedium' weight='bold' mb={1}>
                {t('pageConfiguration:options')}
              </Typography>

              {options.map((x: any, i: number) => (
                <Box key={i} display='flex' flexDirection='column'>
                  {checkShowIfHierarchy(x, options) ? (
                    <React.Fragment key={i}>
                      <EditField
                        option={x}
                        options={options}
                        setOptions={setOptions}
                        i={i}
                        optionType={x.optionType}
                        widgetType={widget.type}
                      />
                    </React.Fragment>
                  ) : null}
                </Box>
              ))}
            </Box>
          )}

          <Box display='flex' flexDirection='row' justifyContent='end'>
            <Button
              type='submit'
              disabled={!widget.type || options.some((o) => o.valid === false)}
              label={t('pageConfiguration:add')}
              sx={{ mt: 3 }}
            />
          </Box>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
